import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { listSubjects } from '../api/subjects';
import { listGradeSections } from '../api/gradeSections';
import { createAssessment, listAssessments, updateAssessment } from '../api/assessments';
import { listSubmissions, markSubmission } from '../api/submissions';
import EducatorLayout from './components/EducatorLayout';

const Assessments = () => {
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [form, setForm] = useState({
    title: '',
    type: 'assessment',
    description: '',
    totalMarks: 0,
    dueDate: null,
    openDate: null,
    closeDate: null,
    lockAfterDueDate: true,
    subjectId: '',
    grade: '',
    sectionId: '',
    attachments: [],
    memo: [],
    quizQuestions: [],
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [openSubmissions, setOpenSubmissions] = useState({});
  const [submissions, setSubmissions] = useState({});
  const [marking, setMarking] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [subj, sect] = await Promise.all([listSubjects(), listGradeSections()]);
        setSubjects(subj || []);
        setSections(sect || []);
        const recent = await listAssessments();
        setAssessments(Array.isArray(recent) ? recent.slice(0, 10) : []);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load references');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'subjectId') {
      const subj = subjects.find((s) => s._id === value);
      if (subj?.grade) setForm((prev) => ({ ...prev, grade: subj.grade }));
    }
  };

  const handleFile = (field, evt) => {
    const files = Array.from(evt.target.files || []);
    setForm((prev) => ({ ...prev, [field]: files }));
  };

  const updateQuestion = (index, changes) => {
    setForm((prev) => {
      const next = [...prev.quizQuestions];
      next[index] = { ...next[index], ...changes };
      return { ...prev, quizQuestions: next };
    });
  };

  const addQuestion = (type = 'mcq') => {
    setForm((prev) => ({
      ...prev,
      quizQuestions: [
        ...prev.quizQuestions,
        type === 'short'
          ? { type: 'short', prompt: '', answer: '' }
          : { type: 'mcq', prompt: '', options: ['', ''], correctIndex: 0 },
      ],
    }));
  };

  const removeQuestion = (index) => {
    setForm((prev) => ({
      ...prev,
      quizQuestions: prev.quizQuestions.filter((_, idx) => idx !== index),
    }));
  };

  const addOption = (qIndex) => {
    setForm((prev) => {
      const next = [...prev.quizQuestions];
      const target = next[qIndex];
      if (!target || target.type !== 'mcq') return prev;
      target.options = [...(target.options || []), ''];
      return { ...prev, quizQuestions: next };
    });
  };

  const updateOption = (qIndex, optIndex, value) => {
    setForm((prev) => {
      const next = [...prev.quizQuestions];
      const target = next[qIndex];
      if (!target || target.type !== 'mcq') return prev;
      const options = [...(target.options || [])];
      options[optIndex] = value;
      target.options = options;
      return { ...prev, quizQuestions: next };
    });
  };

  const removeOption = (qIndex, optIndex) => {
    setForm((prev) => {
      const next = [...prev.quizQuestions];
      const target = next[qIndex];
      if (!target || target.type !== 'mcq') return prev;
      target.options = (target.options || []).filter((_, idx) => idx !== optIndex);
      if (target.correctIndex >= target.options.length) {
        target.correctIndex = Math.max(0, target.options.length - 1);
      }
      return { ...prev, quizQuestions: next };
    });
  };

  const toggleSubmissions = async (assessmentId) => {
    setOpenSubmissions((prev) => ({ ...prev, [assessmentId]: !prev[assessmentId] }));
    if (openSubmissions[assessmentId]) return;
    try {
      const data = await listSubmissions({ assessmentId });
      setSubmissions((prev) => ({ ...prev, [assessmentId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load submissions');
    }
  };

  const handleMarkSubmission = async (submissionId, payload, assessmentId) => {
    try {
      setMarking((prev) => ({ ...prev, [submissionId]: true }));
      await markSubmission(submissionId, payload);
      const data = await listSubmissions({ assessmentId });
 setSubmissions((prev) => ({ ...prev, [assessmentId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to mark submission');
    } finally {
      setMarking((prev) => ({ ...prev, [submissionId]: false }));
    }
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const quizQuestions = (form.quizQuestions || [])
        .map((q) => {
          if (!q?.prompt?.trim()) return null;
          if (q.type === 'short') {
            return { type: 'short', prompt: q.prompt.trim(), answer: q.answer || '' };
          }
          const options = (q.options || []).map((opt) => String(opt || '').trim()).filter(Boolean);
          return { type: 'mcq', prompt: q.prompt.trim(), options, correctIndex: Number(q.correctIndex) || 0 };
        })
        .filter(Boolean);
      const quiz = quizQuestions.length
        ? { type: 'embedded', config: { questions: quizQuestions } }
        : {};

      await createAssessment({
        ...form,
        quiz,
        sectionId: form.sectionId || null,
      });
      const fresh = await listAssessments();
      setAssessments(Array.isArray(fresh) ? fresh.slice(0, 10) : []);
      setForm((prev) => ({
        ...prev,
        title: '',
        description: '',
        totalMarks: 0,
        dueDate: null,
        openDate: null,
        closeDate: null,
        lockAfterDueDate: true,
        attachments: [],
        memo: [],
        quizQuestions: [],
      }));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedSubject = useMemo(() => subjects.find((s) => s._id === form.subjectId), [subjects, form.subjectId]);

  const toggleRelease = async (assessment) => {
    try {
      setSubmitting(true);
      await updateAssessment(assessment._id, {
        release: { released: !assessment.release?.released, releasedAt: new Date().toISOString() },
      });
      const fresh = await listAssessments();
      setAssessments(Array.isArray(fresh) ? fresh.slice(0, 10) : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update release status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModerationStatus = async (assessmentId, status) => {
    try {
      setSubmitting(true);
      await updateAssessment(assessmentId, { moderation: { status } });
      const fresh = await listAssessments();
      setAssessments(Array.isArray(fresh) ? fresh.slice(0, 10) : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update moderation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForModeration = async (assessmentId) => {
    await handleModerationStatus(assessmentId, 'in-review');
  };

  const handleModerationComment = async (assessmentId, comment) => {
    const trimmed = String(comment || '').trim();
    if (!trimmed) return;
    try {
      setSubmitting(true);
      await updateAssessment(assessmentId, { moderationComment: trimmed });
      const fresh = await listAssessments();
 setAssessments(Array.isArray(fresh) ? fresh.slice(0, 10) : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to add moderation comment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EducatorLayout>
      <Box>
        {(loading || submitting) && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={3}>
              <Typography variant="h6" fontWeight={700}>
                Assessment Builder
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Card elevation={1} sx={{ borderRadius: 3, height: '100%' }}>
                    <CardHeader title="New assessment" />
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
                    <TextField
                      label="Title"
                      value={form.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      required
                    />
                    <TextField
                      select
                      label="Type"
                      value={form.type}
                      onChange={(e) => handleChange('type', e.target.value)}
                    >
                      <MenuItem value="quiz">Quiz</MenuItem>
                      <MenuItem value="test">Test</MenuItem>
                      <MenuItem value="assessment">Assessment</MenuItem>
                      <MenuItem value="sba">SBA</MenuItem>
                      <MenuItem value="exam">Exam</MenuItem>
                      <MenuItem value="project">Project</MenuItem>
                    </TextField>
                    <TextField
                      label="Description"
                      value={form.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      multiline
                      minRows={2}
                    />
                    <TextField
                      type="number"
                      label="Total marks"
                      value={form.totalMarks}
                      onChange={(e) => handleChange('totalMarks', e.target.value)}
                    />
                    <TextField
                      select
                      label="Subject"
                      value={form.subjectId}
                      onChange={(e) => handleChange('subjectId', e.target.value)}
                      required
                    >
                      {subjects.map((s) => (
                        <MenuItem key={s._id} value={s._id}>
                          {s.name} (G{s.grade})
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Grade"
                      value={form.grade}
                      onChange={(e) => handleChange('grade', e.target.value)}
                      required
                    >
                      {[...new Set(subjects.map((s) => s.grade))].map((g) => (
                        <MenuItem key={g} value={g}>{`Grade ${g}`}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Section (optional)"
                      value={form.sectionId}
                      onChange={(e) => handleChange('sectionId', e.target.value)}
                    >
                      <MenuItem value="">All sections</MenuItem>
                      {sections
                        .filter((sec) => !form.grade || Number(sec.grade) === Number(form.grade))
                        .map((sec) => (
                          <MenuItem key={sec._id} value={sec._id}>
                            {`${sec.year}-G${sec.grade}${sec.section}`}
                          </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                      label="Due date"
                      type="datetime-local"
                      value={form.dueDate || ''}
                      onChange={(e) => handleChange('dueDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Opens"
                        type="datetime-local"
                        value={form.openDate || ''}
                        onChange={(e) => handleChange('openDate', e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Closes"
                        type="datetime-local"
                        value={form.closeDate || ''}
                        onChange={(e) => handleChange('closeDate', e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                    <TextField
                      select
                      label="Lock after due date"
 value={form.lockAfterDueDate ? 'yes' : 'no'}
                      onChange={(e) => handleChange('lockAfterDueDate', e.target.value === 'yes')}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                    <TextField
                      label="Question paper / resources"
                      type="file"
                      inputProps={{ multiple: true }}
                      onChange={(e) => handleFile('attachments', e)}
                    />
                    <TextField
                      label="Memo"
                      type="file"
                      inputProps={{ multiple: true }}
                      onChange={(e) => handleFile('memo', e)}
                    />
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Quiz form
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add MCQ or short-answer questions for auto-marking.
                      </Typography>
                    </Stack>
                    <Stack spacing={2}>
                      {form.quizQuestions.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No quiz questions yet. Add some if this assessment is a quiz.
                        </Typography>
                      )}
                      {form.quizQuestions.map((question, index) => (
                        <Card key={`${question.type}-${index}`} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ p: 2 }}>
                            <Stack spacing={2}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                <TextField
                                  select
                                  label="Question type"
                                  value={question.type}
                                  onChange={(e) => updateQuestion(index, { type: e.target.value })}
                                  sx={{ minWidth: 160 }}
                                >
                                  <MenuItem value="mcq">MCQ</MenuItem>
                                  <MenuItem value="short">Short answer</MenuItem>
                                </TextField>
                                <Button color="error" variant="outlined" onClick={() => removeQuestion(index)}>
                                  Remove
                                </Button>
                              </Stack>
                              <TextField
                                label={`Question ${index + 1}`}
                                value={question.prompt || ''}
                                onChange={(e) => updateQuestion(index, { prompt: e.target.value })}
                                multiline
                                minRows={2}
                              />
                              {question.type === 'mcq' ? (
                                <Stack spacing={1.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    Options
                                  </Typography>
                                  {(question.options || []).map((option, optIndex) => (
                                    <Stack key={`${index}-opt-${optIndex}`} direction="row" spacing={1}>
                                      <TextField
                                        label={`Option ${optIndex + 1}`}
                                        value={option}
                                        onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                        fullWidth
                                      />
                                      <Button
                                        color="error"
                                        variant="outlined"
                                        onClick={() => removeOption(index, optIndex)}
                                      >
                                        Remove
                                      </Button>
                                    </Stack>
                                  ))}
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <Button variant="outlined" onClick={() => addOption(index)}>
                                      Add option
                                    </Button>
                                    <TextField
                                      select
                                      label="Correct option"
                                      value={question.correctIndex ?? 0}
                                      onChange={(e) => updateQuestion(index, { correctIndex: Number(e.target.value) })}
                                      sx={{ minWidth: 200 }}
                                    >
                                      {(question.options || []).map((_opt, optIndex) => (
                                        <MenuItem key={`${index}-correct-${optIndex}`} value={optIndex}>
                                          Option {optIndex + 1}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Stack>
                                </Stack>
 ) : (
                                <TextField
                                  label="Suggested answer (optional)"
                                  value={question.answer || ''}
                                  onChange={(e) => updateQuestion(index, { answer: e.target.value })}
                                  multiline
                                  minRows={2}
                                />
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button variant="outlined" onClick={() => addQuestion('mcq')}>
                          Add MCQ
                        </Button>
                        <Button variant="outlined" onClick={() => addQuestion('short')}>
                          Add short answer
                        </Button>
                      </Stack>
                    </Stack>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<AssignmentTurnedInIcon />}
                          disabled={submitting}
                        >
                          Publish assessment
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Card elevation={1} sx={{ borderRadius: 3, height: '100%' }}>
                    <CardHeader title="Upcoming assessments" />
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                      <Stack spacing={2}>
                        {assessments.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No assessments yet. Publish to see them here.
                          </Typography>
                        )}
                        {assessments.map((assessment) => (
                          <Card key={assessment._id} elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                              <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography fontWeight={700}>{assessment.title}</Typography>
                                  <Chip size="small" label={assessment.type?.toUpperCase()} />
                                  <Chip size="small" label={`G${assessment.grade}`} />
                                  {assessment.quiz?.config?.questions?.length > 0 && (
                                    <Chip size="small" color="secondary" label="Quiz" />
                                  )}
                                  {assessment.release?.released && <Chip size="small" color="success" label="Marks released" />}
                                  {assessment.lockAfterDueDate && <Chip size="small" color="warning" label="Locked after due" />}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
 Due {assessment.dueDate ? new Date(assessment.dueDate).toLocaleString() : 'TBC'}
                                </Typography>
                                <Typography variant="caption">
                                  Subject: {subjects.find((s) => s._id === assessment.subjectId)?.name || assessment.subjectId}
                                </Typography>
                                <Typography variant="caption">
                                  Moderation: {assessment.moderation?.status || 'pending'}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => toggleRelease(assessment)}
                                    disabled={submitting}
                                  >
                                    {assessment.release?.released ? 'Unrelease marks' : 'Release marks'}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleSubmitForModeration(assessment._id)}
                                    disabled={submitting}
                                  >
                                    Submit to moderator
                                  </Button>
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                    <TextField
                                      select
                                      size="small"
                                      label="Moderation status"
                                      value={assessment.moderation?.status || 'pending'}
                                      onChange={(e) => handleModerationStatus(assessment._id, e.target.value)}
                                    >
                                      <MenuItem value="pending">Pending</MenuItem>
                                      <MenuItem value="in-review">In review</MenuItem>
                                    </TextField>
                                  <TextField
                                    size="small"
                                    label="Add moderation comment"
                                    placeholder="Type comment and press Enter"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleModerationComment(assessment._id, e.target.value);
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                </Stack>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => toggleSubmissions(assessment._id)}
                                >
 {openSubmissions[assessment._id] ? 'Hide submissions' : 'Review submissions'}
                                </Button>
                                {openSubmissions[assessment._id] && (
                                  <Stack spacing={1.5}>
                                    {(submissions[assessment._id] || []).length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">
                                        No submissions yet.
                                      </Typography>
 ) : (
                                     (submissions[assessment._id] || []).map((sub) => (
                                        <Card key={sub._id} variant="outlined" sx={{ borderRadius: 2 }}>
                                          <CardContent>
                                            <Stack spacing={1.5}>
                                              <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography fontWeight={700}>
                                                  {sub.studentId?.name || 'Student'} {sub.studentId?.surname || ''}
                                                </Typography>
                                                <Chip size="small" label={sub.status || 'submitted'} />
                                                {sub.autoScore != null && (
                                                  <Chip size="small" label={`Auto: ${sub.autoScore}`} />
                                                )}
                                                {sub.educatorScore != null && (
                                                  <Chip size="small" color="success" label={`Score: ${sub.educatorScore}`} />
                                                )}
                                              </Stack>
                                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {(sub.files || []).map((file) => (
                                                  <Button
                                                    key={file.key}
                                                    size="small"
                                                    variant="outlined"
                                                    href={file.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                  >
                                                    {file.originalName}
                                                  </Button>
                                                ))}
                                                {(sub.feedbackFiles || []).map((file) => (
                                                  <Button
                                                    key={file.key}
                                                    size="small"
                                                    variant="outlined"
                                                    href={file.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                  >
                                                    Feedback: {file.originalName}
                                                  </Button>
                                                ))}
                                              </Stack>
                                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                                <TextField
                                                  size="small"
                                                  label="Score"
                                                  type="number"
                                                  defaultValue={sub.educatorScore ?? sub.autoScore ?? ''}
                                                  onBlur={(e) =>
                                                    handleMarkSubmission(
                                                      sub._id,
                                                      { educatorScore: e.target.value, status: 'marked' },
                                                      assessment._id
                                                    )
                                                  }
                                                />
                                                <TextField
                                                  size="small"
                                                  label="Feedback comment"
                                                  defaultValue={sub.feedback || ''}
                                                  onBlur={(e) =>
                                                    handleMarkSubmission(
                                                      sub._id,
                                                      { feedback: e.target.value, status: 'returned' },
                                                      assessment._id
                                                    )
                                                  }
                                                  fullWidth
                                                />
                                                <Button component="label" size="small" variant="outlined">
                                                  Upload feedback
                                                  <input
                                                    hidden
                                                    type="file"
                                                    multiple
                                                    onChange={(e) =>
                                                      handleMarkSubmission(
                                                        sub._id,
                                                        { feedbackFiles: Array.from(e.target.files || []) },
                                                        assessment._id
                                                      )
                                                    }
                                                  />
                                                </Button>
                                                {marking[sub._id] && <Typography variant="caption">Saving...</Typography>}
                                              </Stack>
                                            </Stack>
                                          </CardContent>
                                        </Card>
                                      ))
                                    )}
                                  </Stack>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </EducatorLayout>
  );
};

export default Assessments;
